import { IonButton, IonLabel } from '@ionic/react';
import React, { FC, useEffect, useRef } from 'react';
import { APP_TITLE, iconUrl } from '../config/env';
import { usePlayer } from '../data/store/usePlayer';
import { usePlaylist } from '../data/store/usePlaylist';
import { useSongs } from '../data/store/useSongs';
import MediaService from '../services/MediaService';


interface PlayerProps {
  onClick:()=>void
}


const Player: FC<PlayerProps> = ({onClick}) => {
  const { playNow, pauseNow, songIsOver} = usePlayer();
  const { advance ,nowPlayingId} = usePlaylist();
  const { retrieve } = useSongs();
  const playerRef = useRef<HTMLVideoElement>(null);
  const nowPlaying = nowPlayingId && retrieve(nowPlayingId);
  useEffect(() => {
    if (playerRef.current) {
      MediaService.please().setPlayerRef(playerRef);
      document.addEventListener('fullscreenchange', () => {
        // document.fullscreenElement will point to the element that
        // is in fullscreen mode if there is one. If there isn't one,
        // the value of the property is null.
        setTimeout(() => {
          MediaService.please().play();
        }, 500);
      });
    }
  }, [playerRef]);

  useEffect(() => {
    if (nowPlaying && 'mediaSession' in navigator) {
      const artwork = nowPlaying.art?.src ||
        Object.values(nowPlaying.artists || {'backup': {avatar: '/icon.png'}})[0].avatar;

      navigator.mediaSession.metadata = new MediaMetadata({
        title: nowPlaying.title,
        artist: nowPlaying.authors
          ? nowPlaying.authors.map(x => nowPlaying.artists[x].alias).join(" ")
          : Object.values(nowPlaying.artists || {})[0]?.alias || "Unknown Artist",
        album: APP_TITLE,
        artwork: [{ src: artwork||iconUrl('apple-touch-icon.png'), sizes: '512x512', type: 'image/jpeg' }]
      });
    }
  }, [nowPlaying]);

  return (
    <IonButton onClick={onClick} aria-label={nowPlaying ? `Open player: ${nowPlaying.title}` : "Open player"}>
      {/* SON-26: this <video> is the app's audio player (songs are musical
          recordings, not spoken-word, so captions/transcripts don't apply).
          WAVE flagged it as a media element with no accessible affordances, so
          give it an accessible name + title describing what's playing. The song
          title shown in the surrounding UI is the text alternative. */}
      <video
        aria-label={nowPlaying ? `Audio player — now playing: ${nowPlaying.title}` : "Audio player"}
        title={nowPlaying ? `Now playing: ${nowPlaying.title}` : "Audio player"}
        preload='auto'
        playsInline={true}
        webkit-playsinline="true"
        x-webkit-airplay="allow"
        onEnded={async () => {
          songIsOver();
          const nextId = await advance();
          const nextSong = nextId ? retrieve(nextId) : undefined;
          nextSong && MediaService
            .please()
            .setSong(nextSong)
            .then(() => MediaService.please().play());
        }}
        onPlay={() => playNow()}
        onPause={() => pauseNow()}
        controlsList='nodownload'

        id='sadfam-media'
        style={{ height: '100%' }}
        ref={playerRef}
      />
      {/* Visually-hidden text gives the inner native <button> an accessible
          name (button-name) and supplies text content so axe's
          aria-prohibited-attr rule does not flag the host's aria-label. */}
      <IonLabel className="sr-only">
        {nowPlaying ? `Open player: ${nowPlaying.title}` : "Open player"}
      </IonLabel>
    </IonButton>
  );
}

export default React.memo(Player);
